/**
 * CZNA-407 — Settings screen where NK rewords the in-app help without a deploy.
 *
 * Lists every help message (`isHelpMessageId`, 81 at the time of the ticket) with the text
 * users see now. The defaults come from `HelpTextProvider`'s context — the messages as
 * Stripes loaded them, before the override layer — and the stored overrides from the
 * `help_text.overrides` row itself, which carries the `optimisticLock` a save needs.
 *
 * Detail pane driven by `?selected=<message id>`, like the other settings lists. The id
 * stays in the URL only; the screen names a row by where the text appears.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useStripes } from '@folio/stripes/core';
import { Accordion, AccordionSet, Callout, Checkbox, MessageBanner, MultiColumnList, Pane, Paneset, SearchField, } from '@folio/stripes/components';
import NavPaneLayout from '../components/NavPaneLayout';
import { useHelpTextContext } from '../components/HelpTextProvider';
import useModuleSetting from '../hooks/useModuleSetting';
import { overrideProblem, overridesApplyTo } from '../utils/helpTextOverrides';
import HelpTextDetail, { HELP_TEXT_SETTING_KEY } from './HelpTextDetail';
import { buildHelpTextRows, listWording } from './helpTextCatalog';
var WRITE_PERM = 'czna-authorities.module-settings.item.put';
var COLUMNS = ['area', 'text', 'status'];
var COLUMN_WIDTHS = { area: '30%', text: '55%', status: '15%' };
var HelpTextSettings = function () {
    var _a, _b, _c;
    var intl = useIntl();
    var history = useHistory();
    var location = useLocation();
    var stripes = useStripes();
    var canWrite = stripes.hasPerm(WRITE_PERM);
    var calloutRef = React.useRef(null);
    var _d = useState(''), query = _d[0], setQuery = _d[1];
    var _f = useState(false), changedOnly = _f[0], setChangedOnly = _f[1];
    var baseMessages = useHelpTextContext().baseMessages;
    var setting = useModuleSetting(HELP_TEXT_SETTING_KEY);
    var storedValue = useMemo(function () {
        if (!setting.data)
            return null;
        var value = setting.data.value;
        return value && typeof value === 'object' && !Array.isArray(value)
            ? value
            : {};
    }, [setting.data]);
    var allRows = useMemo(function () { return buildHelpTextRows(baseMessages, storedValue !== null && storedValue !== void 0 ? storedValue : {}); }, [baseMessages, storedValue]);
    var statusOf = useCallback(function (row) {
        if (row.stored === undefined)
            return 'default';
        return overrideProblem(row.stored, row.defaultText, intl) === null ? 'changed' : 'invalid';
    }, [intl]);
    // The wording users of THIS locale see: outside Czech a stored change is not applied
    // (`overridesApplyTo`), so the list must not present it as the current text.
    var textOf = useCallback(function (row) { return (statusOf(row) === 'changed' && overridesApplyTo(intl.locale) ? row.stored : row.defaultText); }, [statusOf, intl.locale]);
    var placeOf = useCallback(function (row) {
        var area = intl.formatMessage({ id: "ui-czna-authorities.guidanceTexts.area.".concat(row.area) });
        return row.label ? "".concat(area, " \u2014 ").concat(row.label) : area;
    }, [intl]);
    var rows = useMemo(function () {
        var q = query.trim().toLowerCase();
        return allRows.filter(function (row) { return (!changedOnly || row.stored !== undefined)
            && (!q || textOf(row).toLowerCase().includes(q) || placeOf(row).toLowerCase().includes(q)); });
    }, [allRows, changedOnly, query, textOf, placeOf]);
    var search = new URLSearchParams(location.search);
    var selectedId = (_a = search.get('selected')) !== null && _a !== void 0 ? _a : undefined;
    var selectedRow = allRows.find(function (row) { return row.id === selectedId; });
    var select = useCallback(function (id) {
        var sp = new URLSearchParams(location.search);
        if (id)
            sp.set('selected', id);
        else
            sp.delete('selected');
        history.push({ search: sp.toString() ? "?".concat(sp.toString()) : '' });
    }, [history, location.search]);
    var columnMapping = useMemo(function () { return ({
        area: intl.formatMessage({ id: 'ui-czna-authorities.guidanceTexts.column.area' }),
        text: intl.formatMessage({ id: 'ui-czna-authorities.guidanceTexts.column.text' }),
        status: intl.formatMessage({ id: 'ui-czna-authorities.guidanceTexts.column.status' }),
    }); }, [intl]);
    var formatter = useMemo(function () { return ({
        area: function (row) { return placeOf(row); },
        text: function (row) { return listWording(textOf(row)); },
        status: function (row) { return (React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.status.".concat(statusOf(row)) })); },
    }); }, [placeOf, textOf, statusOf]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Paneset, null,
            React.createElement(Pane, { id: "guidance-texts-filter-pane", defaultWidth: "22%", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.pane.navigation" }) },
                React.createElement(NavPaneLayout, null,
                    React.createElement("hr", { style: { border: 'none', borderTop: '1px solid #d9d9d9', margin: '12px 0' } }),
                    React.createElement(AccordionSet, null,
                        React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.search' }), separator: false },
                            React.createElement(SearchField, { name: "query", value: query, onChange: function (e) { return setQuery(e.target.value); }, onClear: function () { return setQuery(''); }, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.guidanceTexts.search.placeholder' }) }),
                            React.createElement(Checkbox, { label: intl.formatMessage({ id: 'ui-czna-authorities.guidanceTexts.filter.changedOnly' }), checked: changedOnly, onChange: function () { return setChangedOnly(function (v) { return !v; }); } }))))),
            React.createElement(Pane, { id: "guidance-texts-content-pane", defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.title" }), paneSub: intl.formatMessage({ id: 'ui-czna-authorities.guidanceTexts.resultCount' }, { count: rows.length }) },
                !overridesApplyTo(intl.locale) && (React.createElement(MessageBanner, { type: "warning" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.localeNotice" }))),
                setting.isError && (React.createElement(MessageBanner, { type: "error" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.guidanceTexts.error.load" }))),
                React.createElement(MultiColumnList, { contentData: rows, columnMapping: columnMapping, columnWidths: COLUMN_WIDTHS, visibleColumns: COLUMNS, nonInteractiveHeaders: COLUMNS, formatter: formatter, selectedRow: selectedId ? { id: selectedId } : undefined, onRowClick: function (_e, row) { return select(row.id); }, isEmptyMessage: intl.formatMessage({ id: 'ui-czna-authorities.guidanceTexts.empty' }), interactive: true, wrapCells: true })),
            selectedRow && (React.createElement(HelpTextDetail
            // Re-key on the stored text so a save or reset starts the draft afresh.
            , { 
                // Re-key on the stored text so a save or reset starts the draft afresh.
                key: "".concat(selectedRow.id, "\0").concat((_b = selectedRow.stored) !== null && _b !== void 0 ? _b : ''), row: selectedRow, title: placeOf(selectedRow), storedValue: storedValue, optimisticLock: (_c = setting.data) === null || _c === void 0 ? void 0 : _c.optimisticLock, canWrite: canWrite, onClose: function () { return select(null); }, onSaved: function (outcome) {
                    var _a;
                    return (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                        type: 'success',
                        message: intl.formatMessage({ id: "ui-czna-authorities.guidanceTexts.".concat(outcome, ".success") }),
                    });
                } }))),
        React.createElement(Callout, { ref: calloutRef })));
};
export default HelpTextSettings;
